/**
 * Check if a user has permission to access a resource based on their role.
 *
 * @param {Array<string>|boolean} allowedRoles - Array of allowed roles, or boolean.
 *   - Array with specific roles ('admin', 'manager'): only these roles have access
 *   - Array with '*': all roles have access
 *   - Empty array [] or false: no one has access
 * @param {string} userRole - The current role of the user
 * @returns {boolean} - true if the user has permission, false otherwise
 */
export function hasPermission(allowedRoles, userRole) {
  if (!userRole) {
    return false;
  }

  if (allowedRoles === false) {
    return false;
  }

  if (!Array.isArray(allowedRoles) || !allowedRoles.length) {
    return false;
  }

  if (allowedRoles.includes('*')) {
    return true;
  }

  return allowedRoles.includes(userRole);
}

/**
 * Check if a specific field can be displayed to the user
 * based on the service desk fieldsAllowedToDisplay configuration.
 *
 * @param {Object} fieldsAllowedToDisplay - Configuration of allowed fields
 * @param {string} fieldName - Name of the field to check (ex: 'priority')
 * @param {string} userRole - The current role of the user
 * @returns {boolean} - true if the field can be displayed, false otherwise
 */
export function canDisplayField(fieldsAllowedToDisplay, fieldName, userRole) {
  if (!fieldsAllowedToDisplay) {
    return false;
  }

  if (!(fieldName in fieldsAllowedToDisplay)) {
    return false;
  }

  const allowedRoles = fieldsAllowedToDisplay[fieldName];

  if (allowedRoles === false) {
    return false;
  }

  return hasPermission(allowedRoles, userRole);
}

// Re-export, not a second declaration: this file used to hold its own copy and the two had
// drifted apart. `constants/permissions` is the single front-end list.
export { PERMISSIONS } from '../constants/permissions.js';

/**
 * Check if user has a specific permission (granular permission check)
 * @param {Object} user - User object with permissions array
 * @param {string} permission - Permission string to check
 * @returns {boolean}
 */
export const hasUserPermission = (user, permission) => {
  if (!user || !user.permissions) {
    return false;
  }
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  return permissions.includes(permission);
};
